import { computed, defineComponent, nextTick, onMounted, provide, reactive, ref, toRaw } from 'vue'
import AProductSpec from '../components/product-spec/productSpec.vue'
import AProductSpecFreeGift from '../components/product-spec/productSpecFreeGift.vue'
import ASpec from '../components/spec'
import AAddon from '../components/addon'
import ADropShip from '../components/dropShip.vue'
import AImprint, { type AImprintInstance } from '../components/imprint-method'
import AProductionTime from '../components/productionTime.vue'
import AImagePreview, { type Measurable } from '../components/image-preview'
import AFromCart from '../components/fromCart.vue'
import AStepItem, { type StepStatus } from '../components/step-item'
import PreviewTotal from '../components/PreviewTotal.vue'
import '../style/index.less'
import type { TotalFee, OrderModel } from '../types'
import { CustomizeStep } from '../types'
import AStepGroup from '../components/step-group'
import AEventForm from '../components/eventForm.vue'
import {
  getAllProductionTime,
  getBlankLimitation,
  getMaxAssortNum,
  getMaxQty,
  getMinQty,
  getPriceList,
  getSpecListByPriceList,
  getTotalQty,
  initOrderInfo,
  updatePriceQtyForMultiSize,
  useGlobal,
  useLastCart,
  useStep,
  useStock,
  useWindow
} from '../composables'
import { init, initSizeQty } from '../utils'
import { useUpdateView } from '../composables/use-update'
import {
  type ShowPopupPriceGroupProvideOption,
  SHOW_BIG_IMAGE_KEY,
  SHOW_PRICE_GROUP_KEY
} from '../token'
import { ProductSpecModel } from '../model'
import { showPopupPriceGroup } from '../components/popup-price-group'
import bus from '../composables/use-bus'
import { addToCart } from '@/api/SaleCustomizeClient'
import ProductDetailPopup from '@/pages/product-detail-popup/ProductDetailPc.vue'
import { IsLoggedIn } from '@anypromo/shared/api/AccountClient'
import { ApMessageBox, ApButton, ApPopup, ApImage, ApAlert } from '@anypromo/components-pc'
import { useApp } from '../store/modules/app'
import { useProductSpec } from '../store/modules/productSpec'
import { useImprintMethod } from '../store/modules/imprintMethod'
import { useAddon } from '../store/modules/addon'
import { useSpec } from '../store/modules/spec'
import { useDropShip } from '../store/modules/dropShip'
import { type PriceGroupCallback } from '../components/popup-price-group/types'
import { useProductionTime } from '../store/modules/productionTime'
import {
  checkOrder,
  checkSurprise,
  getOrderTotal,
  getProduction,
  setFreeSpecSample,
  loadOrderDetail
} from '@/api/OrderCustomizeClient'
import { useOrder } from '../composables/use-order'
import { OrderType } from '@anypromo/shared/types/SaleCustomize'

export default defineComponent({
  components: {
    AProductSpec,
    AProductSpecFreeGift,
    ASpec,
    AAddon,
    AImprint,
    ApAlert,
    ADropShip,
    AProductionTime,
    AStepItem,
    AStepGroup,
    ApButton,
    ApPopup,
    ApImage,
    AImagePreview,
    AFromCart,
    PreviewTotal,
    ProductDetailPopup,
    AEventForm
  },
  setup () {
    type StepItemInstance = InstanceType<typeof AStepItem>
    const productSpecRef = ref<typeof AProductSpec>()
    const specRef = ref<typeof ASpec>()
    const addonRef = ref<typeof AAddon>()
    const dropShipRef = ref<typeof ADropShip>()
    const imprintRef = ref<AImprintInstance>()
    const ProductionTimeRef = ref<typeof AProductionTime>()

    const stepGroupRef = ref<typeof AStepGroup>()
    const stepProductSpecRef = ref<StepItemInstance>()
    const stepSpecRef = ref<StepItemInstance>()
    const stepAddoncRef = ref<StepItemInstance>()
    const stepImprintRef = ref<StepItemInstance>()
    const stepDropshipRef = ref<StepItemInstance>()

    const productDetailPopupRef = ref<typeof ProductDetailPopup>()

    const submiting = ref(false)
    const customizeEventRef = ref()

    // 订单信息
    const orderSummary = ref('')
    // 是否为platinum
    const isPlatinum = ref(false)
    // 订单total json格式
    const totalFee = ref<TotalFee>()

    // 每步状态
    const stepState = reactive<Record<CustomizeStep, StepStatus>>({
      [CustomizeStep.ChooseProductSpec]: 'process',
      [CustomizeStep.ChooseSpec]: 'wait',
      [CustomizeStep.ChooseAddon]: 'wait',
      [CustomizeStep.ChooseImprint]: 'wait',
      [CustomizeStep.ChooseDropShip]: 'wait',
      [CustomizeStep.ChooseProducttiomTime]: 'wait'
    })

    const {
      productId,
      productName,
      productCategory,
      productSpecList,
      specList,
      addonList,
      isMultiSize,
      supportDropShip,
      defaultArtworkFile,
    } = useWindow()

    const { refer, option, pageStyle, sessionGuid, funcCallback } = useGlobal()

    const appState = useApp()
    const productSpecListState = useProductSpec()
    const specState = useSpec()
    const addonState = useAddon()
    const imprintMethodState = useImprintMethod()
    const dropShipState = useDropShip()
    const productionTimeState = useProductionTime()

    const { getStepNum } = useStep()

    // coupon提示语
    const couponTipMsg = ref('')

    const currentStep = computed<CustomizeStep>({
      get () {
        if (stepState[CustomizeStep.ChooseProductSpec] === 'process')
          return CustomizeStep.ChooseProductSpec
        if (stepState[CustomizeStep.ChooseSpec] === 'process') return CustomizeStep.ChooseSpec
        if (stepState[CustomizeStep.ChooseAddon] === 'process') return CustomizeStep.ChooseAddon
        if (stepState[CustomizeStep.ChooseImprint] === 'process') return CustomizeStep.ChooseImprint
        if (stepState[CustomizeStep.ChooseDropShip] === 'process')
          return CustomizeStep.ChooseDropShip
        return CustomizeStep.ChooseProducttiomTime
      },
      set (val: CustomizeStep) {
        stepState[currentStep.value] = 'wait'
        stepState[val] = 'process'
        if (val === CustomizeStep.ChooseImprint) {
          onUpdateView()

          if (imprintMethodState.getSelectedList.length === 0) {
            if (imprintRef.value) {
              // imprintRef.value.create()
              imprintMethodState.create()
              imprintMethodState.setEditIndex(0)
            }
          } else if (imprintMethodState.getSelectedList.length === 1) {
            // 默认展开第一个
            if (imprintRef.value) {
              imprintMethodState.setEditIndex(0)
            }
          }
          onUpdateView()
        }
      }
    })

    // 存储Use the same settings from the previous item状态

    const DoneCustomize = (): void => {
      if (stepProductSpecRef.value) {
        stepState[CustomizeStep.ChooseProductSpec] = 'finish'
      }
      if (stepSpecRef.value) {
        stepState[CustomizeStep.ChooseSpec] = 'finish'
      }
      if (stepAddoncRef.value) {
        stepState[CustomizeStep.ChooseAddon] = 'finish'
      }

      if (stepImprintRef.value) {
        stepState[CustomizeStep.ChooseImprint] = 'finish'
      }
      if (stepDropshipRef.value) {
        stepState[CustomizeStep.ChooseDropShip] = 'finish'
      }

      stepState[CustomizeStep.ChooseProducttiomTime] = 'process'
      // currentStep.value = CustomizeStep.ChooseProducttiomTime
    }

    const isDone = () => {
      if (stepProductSpecRef.value && stepState[CustomizeStep.ChooseProductSpec] !== 'finish') {
        return false
      }

      if (specList.length > 0 && stepState[CustomizeStep.ChooseSpec] !== 'finish') {
        return false
      }

      if (
        addonList.length > 0 &&
        stepAddoncRef.value &&
        stepState[CustomizeStep.ChooseAddon] !== 'finish'
      ) {
        return false
      }

      if (stepImprintRef.value && stepState[CustomizeStep.ChooseImprint] !== 'finish') {
        return false
      }

      if (
        supportDropShip &&
        stepDropshipRef.value &&
        stepState[CustomizeStep.ChooseDropShip] !== 'finish'
      ) {
        return false
      }

      return true
    }

    const orderData = (window as any).orderData as OrderModel

    // 判断imprint blank的支持情况,1:支持imprint 2:支持blank 3:全部都支持

    appState.dataFill({
      isBlank: false,
      maxAssortNum: 1,
      samePreviousChecked: false,
      samePreviousDisaled: false,
      blankLimitation: getBlankLimitation(orderData.orderType),
      currentPriceList: [],
      maxLocationNum: 999,
      isFreeGift: orderData.isFreeGift,
      orderType: orderData.orderType,
      couponCode: orderData.couponCode,
      customizeEventName: orderData.customizeEventName,
      customizeEventDate: orderData.customizeEventDate,
      customizeEventList: orderData.customizeEventList,
      transitTime: orderData.transitTime
    })

    specState.dataFill(specList)

    addonState.dataFill(addonList)

    // 如果只支持blank, 那默认就是blank选项
    if (appState.getBlankLimitation === 2) {
      appState.setBlankValue(true)
    }

    const popupBigImageOptions = reactive<{
      visible: boolean
      imgText: string
      imgUrl: string
      virtualRef: Measurable | undefined
    }>({
      visible: false,
      imgText: '',
      imgUrl: '',
      virtualRef: undefined
    })

    const { getStockStatus } = useStock()

    provide(SHOW_BIG_IMAGE_KEY, {
      show (imgUrl: string, imgText: string, virtualRef?: Measurable) {
        popupBigImageOptions.imgText = imgText
        popupBigImageOptions.imgUrl = imgUrl
        popupBigImageOptions.visible = true
        popupBigImageOptions.virtualRef = virtualRef
      },
      hide () {
        popupBigImageOptions.visible = false
      }
    })

    provide(
      SHOW_PRICE_GROUP_KEY,
      (options: ShowPopupPriceGroupProvideOption): Promise<PriceGroupCallback> => {
        return new Promise((resolve, reject) => {
          const selectedProductSpecList =
            options.productSpecList ?? productSpecListState.getSelectedList
          const selectedSpecList = options.specList ?? specState.getSelectedList
          const selectedAddonList = options.addonList ?? addonState.getSelectedList
          const selectedImprintMethodList =
            options.imprintMethodList ?? imprintMethodState.getSelectedList

          showPopupPriceGroup({
            orderType: appState.getOrderType,
            isBlank: appState.getBlankValue,
            type: options.type,
            value: options.value,
            productSpecList: selectedProductSpecList,
            specList: selectedSpecList,
            addonList: selectedAddonList,
            imprintMethodList: selectedImprintMethodList,
            callback: (data: PriceGroupCallback) => {
              const productSpecNum = productSpecList.length

              if (options.type === 'productSpec' && productSpecNum === 0) {
                // hideDialog()
                return
              }
              // 是否需要改变step
              const isChangeStep = false

              appState.updatePriceList()
              onUpdateView()
              if (productSpecRef.value) {
                productSpecRef.value.validateQty();
              }

              specList.forEach((group) => {
                const values = group.values.filter((f) => f.enabled)
                if (values.length === 1) {
                  specState.setSelectedValue(group.name, values[0])
                  // group.selectedItem = color
                }
              })

              addonList.forEach((group) => {
                const values = group.values.filter((f) => f.enabled)
                if (values.length === 1) {
                  addonState.setSelectedValue(group.name, values[0])
                }
              })

              // 如果第一步有修改，那后面所有的step，都必须修改
              if (data.changedStep.productSpec) {
                currentStep.value = CustomizeStep.ChooseProductSpec
                if (data.changedStep.spec) {
                  stepState[CustomizeStep.ChooseSpec] = 'wait'
                }
                if (data.changedStep.addon) {
                  stepState[CustomizeStep.ChooseAddon] = 'wait'
                }
                if (data.changedStep.imprintMethod) {
                  stepState[CustomizeStep.ChooseImprint] = 'wait'
                }
              } else if (data.changedStep.spec) {
                currentStep.value = CustomizeStep.ChooseSpec
                if (data.changedStep.addon) {
                  stepState[CustomizeStep.ChooseAddon] = 'wait'
                }
                if (data.changedStep.imprintMethod) {
                  stepState[CustomizeStep.ChooseImprint] = 'wait'
                }
              } else if (data.changedStep.addon) {
                currentStep.value = CustomizeStep.ChooseAddon
                if (data.changedStep.imprintMethod) {
                  stepState[CustomizeStep.ChooseImprint] = 'wait'
                }
              } else if (data.changedStep.imprintMethod) {
                currentStep.value = CustomizeStep.ChooseImprint
              } else {
                // 判断数量，
                if (data.reasonCode === 'min_qty') {
                  // 显示step 1
                  currentStep.value = CustomizeStep.ChooseProductSpec
                }
              }

              if (data.changedStep.productSpec) {
                // 切换到step1
                currentStep.value = CustomizeStep.ChooseProductSpec
              }

              onUpdateView()
              // 只要遇到切换，那就
              resolve(data)
            }
          })
        })
      }
    )

    /**
     * 更新production time
     */
    const updateProductionTime = () => {
      productionTimeState.updateProductionTime()
    }

    let timer: number | undefined
    const comuteTotalDiff = (tryNum: number) => {
      if (tryNum >= 60) return
      getProduction()
        .then((data) => {
          if (data.Data) {
            for (const key in data.Data) {
              productionTimeState.allProductionTime.some((f) => {
                if (f.productionTime.toString() === key.toString()) {
                  if (data.Data) {
                    f.priceDifference = data.Data[key]
                  }
                  return true
                }
                return false
              })
            }
          }
        })
        .catch(() => {
          timer = setTimeout(() => {
            comuteTotalDiff(tryNum + 1)
          }, 300)
        })
    }

    const updateTotal = () => {
      // 计算当前使用的price

      submiting.value = true
      if (timer) {
        clearTimeout(timer)
      }
      checkOrder({
        orderType: appState.getOrderType,
        isFreeGiftCustomize: appState.isFreeGift,
        isFreeSpecSample: false,
        computePriceDifference: isDone(),
        productId: productId,
        isBlank: appState.getBlankValue,
        isMultiSize: isMultiSize,
        pageAction: option,
        selectedProductSpecList: productSpecListState.getSelectedList,
        selectedSpecList: specState.getSelectedList,
        selectedAddonList: addonState.getSelectedList,
        selectedImprintMethodList: imprintMethodState.getSelectedList,
        selectedProductionTime: productionTimeState.getCurrentProductionTime!,
        dropShip: dropShipState.getDropShip,
        couponCode: appState.getCouponCode,
        allPriceList: productionTimeState.getAllProductionTime
      })
        .then((data) => {
          if (data) {
            orderSummary.value = data.Msg
            totalFee.value = data.Data
            couponTipMsg.value = data.Data.couponMsg

            nextTick(() => {
              window.APEvent.emit('scroll')
            })
            if (isDone()) {
              comuteTotalDiff(0)
            }
          }
        })
        .finally(() => {
          submiting.value = false
        })
    }

    const goNext = (): CustomizeStep => {
      if (stepState[CustomizeStep.ChooseProductSpec] !== 'finish') {
        return CustomizeStep.ChooseProductSpec
      }
      if (stepSpecRef.value && stepState[CustomizeStep.ChooseSpec] !== 'finish') {
        return CustomizeStep.ChooseSpec
      }
      if (stepAddoncRef.value && stepState[CustomizeStep.ChooseAddon] !== 'finish') {
        return CustomizeStep.ChooseAddon
      }
      if (stepImprintRef.value && stepState[CustomizeStep.ChooseImprint] !== 'finish') {
        return CustomizeStep.ChooseImprint
      }
      if (stepDropshipRef.value && stepState[CustomizeStep.ChooseDropShip] !== 'finish') {
        return CustomizeStep.ChooseDropShip
      }
      return CustomizeStep.ChooseProducttiomTime
    }

    const onProductSpecContinue = () => {
      // 如果已经完成了，那就重新计算数据
      window.RestartTimeoutTimer()
      onUpdateView()

      stepState[CustomizeStep.ChooseProductSpec] = 'finish'
      // 
      if (stepDropshipRef.value && stepState[CustomizeStep.ChooseDropShip] === 'finish' && dropShipState.getDropShip.needDropShip) {
        stepState[CustomizeStep.ChooseDropShip] = 'wait'
      }
      // 自动处理
      autoProcessSpec();
      autoProcessAddon();

      const nextStep = goNext()

      // 开始计算total信息
      if (nextStep) {
        // stepState[nextStep] = 'process'
        currentStep.value = nextStep
      }
      updateTotal()
    }
    const dataChange = reactive({
      spec: false,
      addon: false,
    })
    const autoProcessSpec = () => {
      if (dataChange.spec) {
        return;
      }
      // 先检查spec
      if (specList.length > 0) {

        const isAll = specList.every(f => {
          return f.values.length == 1;
        })
        // 如果全部都满足条件，那就自动设置为OK
        if (isAll) {
          stepState[CustomizeStep.ChooseSpec] = 'finish'
        }
      }
      dataChange.spec = true
    }
    const autoProcessAddon = () => {
      if (dataChange.addon) {
        return;
      }
      // 先检查spec
      if (addonList.length > 0) {

        const isAll = addonList.every(f => {
          return f.values.length == 1;
        })
        // 如果全部都满足条件，那就自动设置为OK
        if (isAll) {
          stepState[CustomizeStep.ChooseAddon] = 'finish'
        }
      }
      dataChange.addon = true
    }
    const onSpecContinue = async () => {
      window.RestartTimeoutTimer()

      //重新计算库存
      await getStockStatus(productSpecListState.getSelectedList);

      onUpdateView()
      autoProcessAddon();

      stepState[CustomizeStep.ChooseSpec] = 'finish'

      const nextStep = goNext()
      updateTotal()
      if (nextStep) {
        currentStep.value = nextStep
      }
    }

    const onAddonContinue = () => {
      window.RestartTimeoutTimer()
      onUpdateView()

      stepState[CustomizeStep.ChooseAddon] = 'finish'

      const nextStep = goNext()
      updateTotal()

      if (nextStep) {
        currentStep.value = nextStep
      }
    }

    const onSwitchBlank = (blankValue: boolean) => {
      // 先检查能不能切换
      appState.setBlankValue(blankValue)
      window.RestartTimeoutTimer()

      onUpdateView()
    }

    const onImprintMethodBack = (backStep: CustomizeStep) => {
      if (backStep === CustomizeStep.ChooseProductSpec) {
        currentStep.value = backStep
      }
    }

    const onImprintMethodContinue = () => {
      window.RestartTimeoutTimer()
      onUpdateView()

      stepState[CustomizeStep.ChooseImprint] = 'finish'

      //
      appState.setSamePreviousDisaled(true)

      const nextStep = goNext()
      updateTotal()
      currentStep.value = nextStep
    }
    const onDripShipContinue = () => {
      window.RestartTimeoutTimer()

      stepState[CustomizeStep.ChooseDropShip] = 'finish'

      updateTotal()

      const nextStep = goNext()
      if (nextStep) {
        currentStep.value = nextStep
      } else {
        DoneCustomize()
      }
    }

    const onProductSpecUpdated = (val: ProductSpecModel | ProductSpecModel[]) => {


      appState.updatePriceList()

      useUpdateView({
        isBlank: appState.getBlankValue,
        selectedProductSpecList: productSpecListState.getSelectedList,
        selectedSpecList: specState.getSelectedList,
        selectedAddonList: addonState.getSelectedList,
        selectedImprintMethodList: imprintMethodState.getSelectedList,
        priceList: appState.getCurrentPriceList
      })

      updateProductionTime()
      getStockStatus(val)

      if (isMultiSize) {
        updatePriceQtyForMultiSize(
          appState.getCurrentPriceList,
          toRaw(productSpecListState.getSelectedList)
        )
      }
    }

    const onStepChange = (name: CustomizeStep) => {
      currentStep.value = name
    }

    const renderProductSpec = () => {
      if (appState.isFreeGift) {
        return (
          <a-product-spec-free-gift
            productSpecList={productSpecList}
            onUpdateModel={onProductSpecUpdated}
            onGo-next={onProductSpecContinue}
          ></a-product-spec-free-gift>
        )
      }
      return (
        <a-product-spec
          ref={productSpecRef}
          productSpecList={productSpecList}
          onItem-remove={onUpdateView}
          onUpdateModel={onProductSpecUpdated}
          onGo-next={onProductSpecContinue}
        ></a-product-spec>
      )
    }

    const renderSpec = () => {
      if (specList.length === 0) {
        return
      }

      return (
        <a-step-item
          ref={stepSpecRef}
          name={CustomizeStep.ChooseSpec}
          v-model={stepState[CustomizeStep.ChooseSpec]}
          v-slots={{
            title: () => (
              <div>
                <span class="step">Step {getStepNum(CustomizeStep.ChooseSpec)}.</span> Item Options
              </div>
            )
          }}
        >
          <a-spec ref={specRef} onGo-next={onSpecContinue} onUpdate-view={onUpdateView}></a-spec>
        </a-step-item>
      )
    }

    const renderAddon = () => {
      if (addonList.length === 0) {
        return
      }
      return (
        <a-step-item
          ref={stepAddoncRef}
          name={CustomizeStep.ChooseAddon}
          v-model={stepState[CustomizeStep.ChooseAddon]}
          v-slots={{
            title: () => (
              <div>
                <span class="step">Step {getStepNum(CustomizeStep.ChooseAddon)}.</span> Frequently
                Bought Together
              </div>
            )
          }}
        >
          <a-addon
            ref={addonRef}
            onGo-next={onAddonContinue}
            onUpdate-view={onUpdateView}
          ></a-addon>
        </a-step-item>
      )
    }
    const renderDropship = () => {
      if (supportDropShip === false || orderData.orderType == OrderType.SpecSample) {
        return
      }
      return (
        <a-step-item
          ref={stepDropshipRef}
          name={CustomizeStep.ChooseDropShip}
          v-model={stepState[CustomizeStep.ChooseDropShip]}
          v-slots={{
            title: () => (
              <div>
                <span class="step">Step {getStepNum(CustomizeStep.ChooseDropShip)}.</span>{' '}
                Dropshipping Options
              </div>
            )
          }}
        >
          <a-drop-ship ref={dropShipRef} onGo-next={onDripShipContinue}></a-drop-ship>
        </a-step-item>
      )
    }

    const cartInfo = useLastCart()

    appState.setCurrentPriceList(
      getPriceList({
        isBlank: appState.getBlankValue,
        productSpecList: productSpecListState.getSelectedList,
        specList: specState.getSelectedList,
        addonList: addonState.getSelectedList,
        imprintMethodList: imprintMethodState.getSelectedList
      })
    )

    const onUpdateView = () => {
      appState.updatePriceList()

      useUpdateView({
        isBlank: appState.getBlankValue,
        selectedProductSpecList: productSpecListState.getSelectedList,
        selectedSpecList: specState.getSelectedList,
        selectedAddonList: addonState.getSelectedList,
        selectedImprintMethodList: imprintMethodState.getSelectedList,
        priceList: appState.getCurrentPriceList
      })

      updateProductionTime()
    }

    // 支持最大assort 数量
    const maxAssortNum = getMaxAssortNum(
      getSpecListByPriceList(appState.getCurrentPriceList, productSpecList, [], [])
    )

    appState.setMaxAssortNum(maxAssortNum)

    const onUpdateProductionTime = () => {
      updateTotal()
    }

    const handleVipFreeSpecSample = (checked: boolean) => {
      submiting.value = true
      setFreeSpecSample(1, checked ? 1 : 0)
        .then((data) => {
          if (data) {
            orderSummary.value = data.Msg
            totalFee.value = data.Data
          }
        })
        .finally(() => {
          submiting.value = false
        })
    }
    const handleAdminFreeSpecSample = (checked: boolean) => {
      submiting.value = true
      setFreeSpecSample(2, checked ? 1 : 0)
        .then((data) => {
          if (data) {
            orderSummary.value = data.Msg
            totalFee.value = data.Data
          }
        })
        .finally(() => {
          submiting.value = false
        })
    }
    const handleSurprise = (checked: boolean) => {
      submiting.value = true
      checkSurprise({
        isSurprise: checked ? 1 : 0
      })
        .then((data) => {
          if (data) {
            orderSummary.value = data.Msg
            totalFee.value = data.Data
          }
        })
        .finally(() => {
          submiting.value = false
        })
    }

    const onFromCartChange = (val: boolean) => {
      appState.samePreviousChecked = val
    }

    const renderPreviousImage = () => (
      <a-from-cart
        checked={appState.samePreviousChecked}
        disabled={appState.samePreviousDisaled}
        previousImage={cartInfo?.PreviousImage}
        onChange={onFromCartChange}
      ></a-from-cart>
    )

    const onAddToCart = () => {
      if (stepProductSpecRef.value && stepState[CustomizeStep.ChooseProductSpec] !== 'finish') {
        ApMessageBox.alert(
          'You are still working on Step 1: Product Options.<br /> In order to proceed, you must click the "Continue" button to submit your selections.',
          ' ',
          {
            showClose: false
          }
        )
        return
      }
      if (specList.length > 0 && stepState[CustomizeStep.ChooseSpec] !== 'finish') {
        ApMessageBox.alert(
          `You are still working on Step ${getStepNum(
            CustomizeStep.ChooseSpec
          )}: Item Options.<br /> In order to proceed, you must click the "Continue" button to submit your selections.`,
          ' ',
          {
            showClose: false
          }
        )
        return
      }
      if (addonList.length > 0 && stepState[CustomizeStep.ChooseAddon] !== 'finish') {
        ApMessageBox.alert(
          `You are still working on Step ${getStepNum(
            CustomizeStep.ChooseAddon
          )}: Frequently Bought Together.<br /> In order to proceed, you must click the "Continue" button to submit your selections.`,
          ' ',
          {
            showClose: false
          }
        )
        return
      }

      if (stepImprintRef.value && stepState[CustomizeStep.ChooseImprint] !== 'finish') {
        ApMessageBox.alert(
          `You are still working on Step ${getStepNum(
            CustomizeStep.ChooseImprint
          )}: Imprint Options.<br /> In order to proceed, you must click the "Continue" button to submit your selections.`,
          ' ',
          {
            showClose: false
          }
        )
        return
      }
      if (
        supportDropShip &&
        stepDropshipRef.value &&
        stepState[CustomizeStep.ChooseDropShip] !== 'finish'
      ) {
        ApMessageBox.alert(
          `You are still working on Step ${getStepNum(
            CustomizeStep.ChooseDropShip
          )}: Drop ship.<br /> In order to proceed, you must click the "Continue" button to submit your selections.`,
          ' ',
          {
            showClose: false
          }
        )
        return
      }

      let customizeEventName = ''
      let customizeEventDate = ''
      if (customizeEventRef.value) {

        if (!customizeEventRef.value.checkError()) {
          return
        }

        customizeEventName = customizeEventRef.value.eventName
        customizeEventDate = customizeEventRef.value.eventDate
      }

      IsLoggedIn().then((isLogged) => {
        if (isLogged) {
          submiting.value = true

          window.dataLayer.push({ ecommerce: null })
          window.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
              add: {
                products: [
                  {
                    id: productId,
                    name: productName
                  }
                ]
              },
              items: [
                {
                  item_id: productId,
                  item_name: productName,
                  item_category: productCategory,
                  index: 0,
                  price: (totalFee.value?.totalAmount ?? 0) / (totalFee.value?.totalQty ?? 1),
                  quantity: totalFee.value?.totalQty
                }
              ]
            },
            product: {
              id: productId,
              category: productCategory,
              name: productName
            },
            totalAmt: totalFee.value?.totalAmount
          })

          addToCart(1, {
            couponCode: appState.getCouponCode,
            customizeEventName: customizeEventName,
            customizeEventDate: customizeEventDate,
          })
            .then((data: any) => {
              let redirectUrl = ''
              if (data.couponCode) {
                redirectUrl = 'ShoppingCart.aspx?act=apply&Type=SA&code=' + data.couponCode
              } else {
                redirectUrl = 'ShoppingCart.aspx'
              }
              if (window.top) {
                window.top.location.href = redirectUrl
              } else {
                window.location.href = redirectUrl
              }
            }).catch((data) => {

              ApMessageBox.alert(
                data.Msg,
                ' ',
                {
                  showClose: false
                }
              )
            })
            .finally(() => {
              submiting.value = false
            })
        } else {
          window.ShowLoginAndResisterDIV(
            'loginCallbackAddtoCart',
            sessionGuid,
            window.location.href + '&full=1&isAddPlatinum=' + (isPlatinum.value ? 1 : 0),
            'AddCustomer_SACustomize'
          )
          submiting.value = false
        }
      })
    }

    submiting.value = true

    // 先初始化size
    initSizeQty(appState.getBlankValue, isMultiSize)

    if (refer === 'edit') {
      submiting.value = true
      loadOrderDetail({
        ProductId: productId
      })
        .then((data: any) => {
          initOrderInfo(data.Data.OrderInfo)

          /*
          更新production time */

          const allProductionTimes = getAllProductionTime(appState.getBlankValue)
          const currentProductionTime = allProductionTimes.find(
            (f) => f.productionTime === data.Data.OrderInfo.ProductionTime
          )


          productionTimeState.setAllProductionTime(allProductionTimes)
          if (currentProductionTime) {
            productionTimeState.setCurrentProductionTime(currentProductionTime)
          }

          // 更新结束

          if (productSpecListState.getSelectedList.length > 0) {
            productSpecListState.getSelectedList.forEach((f) => {
              onProductSpecUpdated(f)
            })
          }

          if (appState.maxAssortNum === 1) {
            if (productSpecListState.getSelectedList.length > 0) {

              productSpecRef.value?.setValue(productSpecListState.getSelectedList[0].productSpec.map(f => {
                return {
                  name: f.name,
                  value: f.value
                };
              }));
            }
          }

          orderSummary.value = data.Msg
          totalFee.value = data.Data.Total

          onUpdateView()

          // 设置所有的都完成
          if (appState.isFreeGift) {
            // 判断step 1是否有数据
            const isFreeGiftCustomized =
              productSpecListState.getSelectedList.every((item) => {
                return item.productSpec.every((spec) => {
                  return spec.value
                })
              }) && imprintMethodState.getSelectedList.length > 0
            if (isFreeGiftCustomized) {
              DoneCustomize()
            }
          } else {
            DoneCustomize()
          }
          nextTick(() => {
            window.APEvent.emit('scroll')
          })
        })
        .finally(() => {
          submiting.value = false
        })
    } else if (funcCallback !== '') {
      submiting.value = true
      loadOrderDetail({
        ProductId: productId,
        func: funcCallback
      })
        .then((data: any) => {
          initOrderInfo(data.Data.OrderInfo)
          orderSummary.value = data.Msg
          totalFee.value = data.Data.Total

          onUpdateView()
          // 设置所有的都完成
          DoneCustomize()

          if (appState.maxAssortNum === 1) {
            if (productSpecListState.getSelectedList.length > 0) {

              productSpecRef.value?.setValue(productSpecListState.getSelectedList[0].productSpec.map(f => {
                return {
                  name: f.name,
                  value: f.value
                };
              }));
            }
          }

          nextTick(() => {
            window.APEvent.emit('scroll')
          })

          bus.emit('addToCart')
        })
        .finally(() => {
          submiting.value = false
        })
    } else {
      //
      const orderData = useOrder()
      appState.setBlankValue(orderData.imprintSysNo === -999999)

      init(appState.getBlankValue)
      if (productSpecListState.getSelectedList.length > 0) {
        onProductSpecUpdated(productSpecListState.getSelectedList[0])
      }

      onUpdateView()
      submiting.value = true
      getOrderTotal()
        .then((data) => {
          if (data) {
            orderSummary.value = data.Msg
            totalFee.value = data.Data
            nextTick(() => {
              window.APEvent.emit('scroll')
            })
          }
        })
        .finally(() => {
          submiting.value = false
        })
    }

    bus.on('addToCart', () => {
      onAddToCart()
    })

    bus.on('loginCallbackAddtoCart', () => {
      // .value = true
      onAddToCart()
    })
    // bus.on('InventoryLoaded', () => {
    //   // .value = true
    //   console.log('InventoryLoaded')
    //   updateProductionTime()
    // })

    onMounted(() => {
      if (productSpecListState.getSelectedList.length > 0 && isMultiSize) {
        // 要计算数量了
        updatePriceQtyForMultiSize(
          appState.getCurrentPriceList,
          productSpecListState.getSelectedList
        )
      }
      if (appState.maxAssortNum === 1) {
        if (productSpecListState.getSelectedList.length > 0) {

          productSpecRef.value?.setValue(productSpecListState.getSelectedList[0].productSpec.map(f => {
            return {
              name: f.name,
              value: f.value
            };
          }));
        }
      } else if (appState.maxAssortNum > 1) {

        productSpecRef.value?.setValue(productSpecList.map(f => {
          return {
            name: f.name,
            value: f.values.find(val => val.value === '-1')
          };
        }));
      }
    })

    const onProducTitleClick = () => {
      if (productDetailPopupRef.value) {
        productDetailPopupRef.value.showMe(productId, totalFee.value?.ProductImage ?? '')
      }
    }

    const onSubmitMouseover = () => {
      if (!isDone()) {
        const submitbtn = document.getElementById('submitbtn')
        window.ec.dialog.tips(
          'Please complete the steps on the left before continuing',
          submitbtn,
          {
            custom_class: 'ap-dialog-blue-tips',
            maxWidth: 350
          }
        )
      }
    }
    const onSubmitMouseout = () => {
      window.ec.dialog.closeAll('tips')
    }

    const onStepBeforeEnter = () => {
      nextTick(() => {
        window.APEvent.emit('before-enter')
      })
    }
    const onStepAfterEnter = () => {
      nextTick(() => {
        window.APEvent.emit('after-enter')
      })
    }

    bus.on('InventoryLoaded', async () => {
      await getStockStatus(productSpecListState.getSelectedList);
      updateProductionTime();
    });

    const renderTotalHeader = () => {
      const productImage = productSpecListState.getSelectedList.length > 0 ?
        productSpecListState.getSelectedList[productSpecListState.getSelectedList.length - 1].previewImage
        : totalFee.value?.ProductImage

      return (<div class="order-image clearfix">
        <img id="specImageRight" src={productImage ? productImage : totalFee.value?.ProductImage} />
        <span class="order-head-title">
          {pageStyle == 'less' ? (<span
            style="display: block; color:#231f20;"
            title={productName}
          >
            {productName}
          </span>) : (<a
            class=""
            onClick={onProducTitleClick}
            href="javascript:void(0)"
            style="display: block; color:#231f20;"
            title={productName}
          >
            {productName}
          </a>)}

          <b>#{productId}</b>
        </span>
      </div>);
    }

    return () => (
      <div class="layout row page-main">
        <main class="page-left">
          <div class="page-title">Customize Your Promotional Products</div>
          {cartInfo ? renderPreviousImage() : ''}
          <a-step-group
            ref={stepGroupRef}
            onChange={onStepChange}
            onBeforeEnter={onStepBeforeEnter}
            onAfterEnter={onStepAfterEnter}
          >
            <a-step-item
              ref={stepProductSpecRef}
              name={CustomizeStep.ChooseProductSpec}
              v-model={stepState[CustomizeStep.ChooseProductSpec]}
              v-slots={{
                title: () => (
                  <div>
                    <span class="step">Step {getStepNum(CustomizeStep.ChooseProductSpec)}.</span>{' '}
                    Product Options
                  </div>
                )
              }}
            >
              {renderProductSpec()}
            </a-step-item>

            {renderSpec()}
            {renderAddon()}

            <a-step-item
              ref={stepImprintRef}
              name={CustomizeStep.ChooseImprint}
              v-model={stepState[CustomizeStep.ChooseImprint]}
              v-slots={{
                title: () => (
                  <div>
                    <span class="step">Step {getStepNum(CustomizeStep.ChooseImprint)}.</span>{' '}
                    Imprint Options
                  </div>
                )
              }}
            >
              <a-imprint
                ref={imprintRef}
                onSwitchBlank={onSwitchBlank}
                onItem-removed={onUpdateView}
                onGo-back={onImprintMethodBack}
                onGo-next={onImprintMethodContinue}
              ></a-imprint>
            </a-step-item>
            {renderDropship()}

            <a-step-item
              name={CustomizeStep.ChooseProducttiomTime}
              v-model={stepState[CustomizeStep.ChooseProducttiomTime]}
              v-slots={{
                title: () => (
                  <div>
                    <span class="step">
                      Step {getStepNum(CustomizeStep.ChooseProducttiomTime)}.
                    </span>{' '}
                    {appState.getIsFreeGift ? 'Production Time' : 'Est. Delivery Date'}
                  </div>
                )
              }}
            >
              <a-production-time
                ref={ProductionTimeRef}
                {...{
                  onChange: onUpdateProductionTime
                }}
              ></a-production-time>
              <a-event-form
                ref={customizeEventRef}
                customizeEventName={appState.getCustomizeEventName}
                customizeEventDate={appState.getCustomizeEventDate}
                customizeEventList={appState.getCustomizeEventList}
              ></a-event-form>
            </a-step-item>
          </a-step-group>
          {couponTipMsg.value ? (
            <ap-alert
              type={couponTipMsg.value.includes('successfully') ? 'success' : 'error'}
              effect="dark"
              showIcon={true}
              title={couponTipMsg.value}
            >
              <span class="error-label" innerHTML={couponTipMsg.value}></span>
            </ap-alert>
          ) : (
            ''
          )}
        </main>
        <aside class="page-right">
          <dl class="order-total">
            <dt class="order-total__head">
              <h2>Summary </h2>
              {renderTotalHeader()}
            </dt>
            <dd class="order-total__body">
              <div class="summary-scroll" id="order-summary" innerHTML={orderSummary.value}></div>
              <div class="order-total__subtotal">
                <preview-total
                  totalFee={totalFee.value}
                  onSetVipFreeSpecSampe={handleVipFreeSpecSample}
                  onSetAdminFreeSpecSample={handleAdminFreeSpecSample}
                  onSetSurprise={handleSurprise}
                ></preview-total>
                {/* <div id="order-total" class="order-subtotal clearfix" innerHTML={totalFee.value?.TotalText}>
                </div> */}
                <div class="add-buttons clearfix">
                  <div class="total-footer">
                    <div class="total-footer-left">
                      <div class="total-loading" v-show={submiting.value}>
                        <img src="https://img66.anypromo.com/frontimg/2023/Icon/loader2.gif" />
                      </div>
                    </div>
                    <div
                      class="total-btn"
                      onMouseover={onSubmitMouseover}
                      onMouseout={onSubmitMouseout}
                    >
                      <ap-button
                        type="primary"
                        size="large"
                        id="submitbtn"
                        onClick={onAddToCart}
                        disabled={submiting.value}
                      >
                        {refer === 'edit' ? 'Update' : 'Add to Cart'}
                        <i class="ap-btn-rgt-arw"></i>
                      </ap-button>
                    </div>
                  </div>
                </div>
              </div>
            </dd>
          </dl>
        </aside>
        {pageStyle != 'less' ? (<product-detail-popup ref={productDetailPopupRef}></product-detail-popup>) : ''}
        <a-image-preview
          visible={popupBigImageOptions.visible}
          virtualRef={popupBigImageOptions.virtualRef}
          text={popupBigImageOptions.imgText}
          imgUrl={popupBigImageOptions.imgUrl}
        />
      </div>
    )
  }
})
